/**
 * Format investment amount to display format (K, L, Cr)
 * @param {string|number} value - The investment amount
 * @returns {string} Formatted investment string
 */
export function formatInvestment(value) {
  if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
    return '--'
  }
  
  // If already contains K, L, Cr, return as is
  if (typeof value === 'string' && /[KLCr]/i.test(value)) {
    return value
  }
  
  // Handle range: e.g. '100000-500000'
  if (typeof value === 'string' && value.includes('-')) {
    const [min, max] = value.split('-').map(v => v.trim())
    return formatSingleInvestment(min) + ' - ' + formatSingleInvestment(max)
  }
  
  return formatSingleInvestment(value)
}

/**
 * Format a single investment value
 * @param {string|number} value - The investment amount
 * @returns {string} Formatted investment string
 */
export function formatSingleInvestment(value) {
  let num = Number(String(value).replace(/[^\d.]/g, ''))
  if (isNaN(num)) return value
  
  if (num >= 10000000) {
    return '₹' + (num / 10000000).toFixed(2).replace(/\.00$/, '') + ' Cr'
  }
  if (num >= 100000) {
    return '₹' + (num / 100000).toFixed(2).replace(/\.00$/, '') + ' L'
  }
  if (num >= 1000) {
    return '₹' + (num / 1000).toFixed(2).replace(/\.00$/, '') + ' K'
  }
  return '₹' + num
}

/**
 * Format ROI in months (without appending "months" since label already indicates it)
 * @param {string|number} roiMin - The minimum ROI in months
 * @param {string|number} roiMax - The maximum ROI in months
 * @returns {string} Formatted ROI string
 */
export function formatRoi(roiMin, roiMax = null) {
  if (roiMin === 0 || roiMin === '0' || roiMin === '' || roiMin === null || roiMin === undefined) {
    return '--'
  }
  
  if (roiMax && roiMax !== roiMin && roiMax !== 0 && roiMax !== '0' && roiMax !== '' && roiMax !== null && roiMax !== undefined) {
    return `${roiMin} - ${roiMax}`
  }
  
  return `${roiMin}`
}

/**
 * Format franchise outlets count
 * @param {string|number} outlets - The outlets count
 * @returns {string} Formatted outlets string
 */
export function formatOutlets(outlets) {
  if (outlets === null || outlets === undefined || outlets === '' || outlets === 0) {
    return '--'
  }
  return outlets.toString()
}

/**
 * Map API franchise data to component props
 * @param {Object} franchise - The franchise data from API
 * @returns {Object} Mapped franchise data for components
 */
export function mapFranchiseData(franchise) {
  return {
    id: franchise.id,
    name: franchise.business_name,
    logo: franchise.business_logo,
    category: franchise.category,
    investment: franchise.investment_min,
    investmentMax: franchise.investment_max,
    expectedRoi: franchise.excepted_roi_min,
    expectedRoiMax: franchise.excepted_roi_max,
    outlets: franchise.total_outlets,
    description: franchise.description,
    images: franchise.images,
    establishmentYear: franchise.establishment_year,
    headquarters: franchise.headquarters,
    modelType: franchise.model_type,
    trendingType: franchise.trending_type,
    contactName: franchise.contact_name,
    contactEmail: franchise.contact_email,
    contactPhone: franchise.contact_phone,
    spaceRequired: franchise.space_required,
    territoryDetails: franchise.territory_details,
    franchiseFee: franchise.franchise_fee,
    royaltyPercentage: franchise.royalty_percentage,
    contractDuration: franchise.contract_duration
  }
} 